import { mapGetters } from 'vuex';
import styled, { css } from 'vue-styled-components';

import Dropdown from '@/components/Common/Dropdown';
import StatusInput from '@/components/Common/StatusInput';
import { OPTIONS } from '@/mixins/connection';

import { AssetService } from '@/services';

import COLORS from '@design/colors.js';
import { CONNECTION_TYPES, DEFAULT_SVG_VIEWBOX } from '@consts';
import { generateId, isValueExist } from '@/utils/utils';
import { ON_MOBILE } from '@/styles/common.styled';

import RoundIcludeIcon from '@svgIcons/round_include.svg';
import RoundExcludeIcon from '@svgIcons/round_exclude.svg';
import CloseIcon from '@svgIcons/close.svg';

export const checkRemarketingDirty = ({ inclusions = [], exclusions = [] }) => {
    const checkItem = ({ template, days, filter }) => {
            const noValue =
                    (template === MAIN_TYPES[PIXEL].VISITORS_BY_URL ||
                        template === MAIN_TYPES[PIXEL].FROM_EVENT_USERS ||
                        template === MAIN_TYPES[FB_APP].FROM_EVENT_USERS) &&
                    filter.value === '',
                noDays = !days;
            return noValue || noDays;
        },
        mergedArr = [...inclusions, ...exclusions],
        hasDirty = R.find(checkItem, mergedArr);
    return !!hasDirty;
};

const { FB_APP, FB_PAGE, PIXEL } = CONNECTION_TYPES,
    TEXT_BY_TYPES = R.compose(
        R.pluck('text'),
        R.indexBy(R.prop('type'))
    )(OPTIONS);

const MAIN_TYPES = {
    [PIXEL]: {
        ALL_VISITORS: 'ALL_VISITORS',
        VISITORS_BY_URL: 'VISITORS_BY_URL',
        TOP_TIME_SPENDERS: 'TOP_TIME_SPENDERS',
        FROM_EVENT_USERS: 'FROM_EVENT_USERS',
    },
    [FB_APP]: {
        APP_LAUNCHED_USERS: 'MACA_APP_LAUNCHED_USERS',
        MOST_ACTIVE_USERS: 'MACA_MOST_ACTIVE_USERS',
        TOP_PURCHASE_USERS: 'MACA_TOP_PURCHASE_USERS',
        FROM_EVENT_USERS: 'MACA_FROM_EVENT_USERS',
    },
    [FB_PAGE]: {
        ENGAGED_USER: 'ENGAGED_USER',
        VISITED_USER: 'VISITED_USER',
        CTA_USER: 'CTA_USER',
        MESSAGE_USER: 'MESSAGE_USER',
        SAVED_USER: 'SAVED_USER',
    },
};

const SUB_TYPES = {
    [PIXEL]: {
        [MAIN_TYPES[PIXEL].ALL_VISITORS]: {
            template: MAIN_TYPES[PIXEL].ALL_VISITORS,
            days: '60',
            filter: {
                field: 'url',
                operator: 'i_contains',
                value: '',
            },
        },
        [MAIN_TYPES[PIXEL].VISITORS_BY_URL]: {
            template: MAIN_TYPES[PIXEL].VISITORS_BY_URL,
            days: '60',
            filter: {
                field: 'url',
                operator: 'i_contains',
                value: '',
            },
        },
        [MAIN_TYPES[PIXEL].TOP_TIME_SPENDERS]: {
            template: MAIN_TYPES[PIXEL].TOP_TIME_SPENDERS,
            days: '60',
            filter: {
                field: 'url',
                operator: 'i_contains',
                value: '',
            },
            aggregation: {
                type: 'time_spent',
                method: 'percentile',
                operator: 'in_range',
                value: {
                    from: 75,
                    to: 100,
                },
            },
        },
        [MAIN_TYPES[PIXEL].FROM_EVENT_USERS]: {
            template: MAIN_TYPES[PIXEL].FROM_EVENT_USERS,
            days: '60',
            filter: {
                field: 'event',
                operator: 'eq',
                value: '',
            },
        },
    },
    [FB_APP]: {
        [MAIN_TYPES[FB_APP].APP_LAUNCHED_USERS]: {
            template: MAIN_TYPES[FB_APP].APP_LAUNCHED_USERS,
            days: '60',
            filter: {
                field: 'event',
                operator: 'eq',
                value: 'fb_mobile_activate_app',
            },
        },
        [MAIN_TYPES[FB_APP].MOST_ACTIVE_USERS]: {
            template: MAIN_TYPES[FB_APP].MOST_ACTIVE_USERS,
            days: '60',
            filter: {
                field: 'event',
                operator: 'eq',
                value: 'fb_mobile_activate_app',
            },
            aggregation: {
                type: 'count',
                method: 'percentile',
                operator: 'in_range',
                value: {
                    from: 75,
                    to: 100,
                },
            },
        },
        [MAIN_TYPES[FB_APP].TOP_PURCHASE_USERS]: {
            template: MAIN_TYPES[FB_APP].TOP_PURCHASE_USERS,
            days: '60',
            filter: {
                field: 'event',
                operator: 'eq',
                value: 'fb_mobile_purchase',
            },
            aggregation: {
                type: 'sum',
                field: '_valueToSumInUSD',
                method: 'percentile',
                operator: 'in_range',
                value: {
                    from: 75,
                    to: 100,
                },
            },
        },
        [MAIN_TYPES[FB_APP].FROM_EVENT_USERS]: {
            template: MAIN_TYPES[FB_APP].FROM_EVENT_USERS,
            days: '60',
            filter: {
                field: 'event',
                operator: 'eq',
                value: '',
            },
        },
    },
    [FB_PAGE]: {
        [MAIN_TYPES[FB_PAGE].ENGAGED_USER]: {
            template: MAIN_TYPES[FB_PAGE].ENGAGED_USER,
            days: '60',
            filter: {
                field: 'event',
                operator: 'eq',
                value: 'page_engaged',
            },
        },
        [MAIN_TYPES[FB_PAGE].VISITED_USER]: {
            template: MAIN_TYPES[FB_PAGE].VISITED_USER,
            days: '60',
            filter: {
                field: 'event',
                operator: 'eq',
                value: 'page_post_interaction',
            },
        },
        [MAIN_TYPES[FB_PAGE].CTA_USER]: {
            template: MAIN_TYPES[FB_PAGE].CTA_USER,
            days: '60',
            filter: {
                field: 'event',
                operator: 'eq',
                value: 'page_cta_clicked',
            },
        },
        [MAIN_TYPES[FB_PAGE].MESSAGE_USER]: {
            template: MAIN_TYPES[FB_PAGE].MESSAGE_USER,
            days: '60',
            filter: {
                field: 'event',
                operator: 'eq',
                value: 'page_messaged',
            },
        },
        [MAIN_TYPES[FB_PAGE].SAVED_USER]: {
            template: MAIN_TYPES[FB_PAGE].SAVED_USER,
            days: '60',
            filter: {
                field: 'event',
                operator: 'eq',
                value: 'page_or_post_save',
            },
        },
    },
};

const DROPDOWN_ITEM = {
    [PIXEL]: [
        {
            type: MAIN_TYPES[PIXEL].ALL_VISITORS,
            text: 'REMARKETING.main_type_pixel_all_visitor',
        },
        {
            type: MAIN_TYPES[PIXEL].VISITORS_BY_URL,
            text: 'REMARKETING.main_type_pixel_all_visitors_by_url',
        },
        {
            type: MAIN_TYPES[PIXEL].TOP_TIME_SPENDERS,
            text: 'REMARKETING.main_type_pixel_top_time_spenders',
        },
        {
            type: MAIN_TYPES[PIXEL].FROM_EVENT_USERS,
            text: 'REMARKETING.main_type_pixel_from_event_users',
        },
    ],
    [FB_APP]: [
        {
            type: MAIN_TYPES[FB_APP].APP_LAUNCHED_USERS,
            text: 'REMARKETING.main_type_app_app_launcehd_users',
        },
        {
            type: MAIN_TYPES[FB_APP].MOST_ACTIVE_USERS,
            text: 'REMARKETING.main_type_app_most_active_users',
        },
        {
            type: MAIN_TYPES[FB_APP].TOP_PURCHASE_USERS,
            text: 'REMARKETING.main_type_app_top_purchase_users',
        },
        {
            type: MAIN_TYPES[FB_APP].FROM_EVENT_USERS,
            text: 'REMARKETING.main_type_app_from_event_users',
        },
    ],
    [FB_PAGE]: [
        {
            type: MAIN_TYPES[FB_PAGE].ENGAGED_USER,
            text: 'REMARKETING.main_type_page_engaged_user',
        },
        {
            type: MAIN_TYPES[FB_PAGE].VISITED_USER,
            text: 'REMARKETING.main_type_page_visited_user',
        },
        {
            type: MAIN_TYPES[FB_PAGE].CTA_USER,
            text: 'REMARKETING.main_type_page_cta_user',
        },
        {
            type: MAIN_TYPES[FB_PAGE].MESSAGE_USER,
            text: 'REMARKETING.main_type_page_message_user',
        },
        {
            type: MAIN_TYPES[FB_PAGE].SAVED_USER,
            text: 'REMARKETING.main_type_page_saved_user',
        },
    ],
};

const SUB_DROPDOWN_ITEM = {
    percentile: [
        {
            value: 75,
            text: '25%',
        },
        {
            value: 90,
            text: '10%',
        },
        {
            value: 95,
            text: '5%',
        },
    ],
    keywords: [
        {
            value: 'i_contains',
            text: 'REMARKETING.dropdown_keywords_contains',
        },
        {
            value: 'i_not_contains',
            text: 'REMARKETING.dropdown_keywords_do_not_contain',
        },
        {
            value: 'eq',
            text: 'REMARKETING.dropdown_keywords_equals',
        },
    ],
};

const MAX_NUM_OF_INCLUDED = 3,
    MAX_NUM_OF_EXCLUDED = 1;

const INCLUSIONS = 'inclusions',
    EXCLUSIONS = 'exclusions';

export const makeDefaultOriginData = ({ assets, baseType }) => {
    const assetsObj = R.indexBy(R.prop('type'), assets);

    if (baseType.toUpperCase() === 'WEB') {
        const pixel = assetsObj[PIXEL];
        const page = assetsObj[FB_PAGE];

        return isValueExist(pixel) ? pixel : page;
    } else {
        const sdk = assetsObj[FB_APP];

        return sdk;
    }
};

export default {
    name: 'Remarketing',
    data() {
        return {
            eventsDropdown: [],
            dropdownLoading: false,
            mountLoading: true,
        };
    },
    mounted() {
        setTimeout(() => {
            this.mountLoading = false;
        }, 400);
    },
    props: {
        data: {
            type: Object,
            default: R.always({}),
        },
        type: {
            type: String,
            default: 'REMARKETING', // LOOKALIKE
        },
        dirty: {
            type: Boolean,
            default: false,
        },
    },
    watch: {
        mainSource: {
            handler(newSource, oldSource) {
                const { id: newId, type } = newSource;
                const { id: oldId } = oldSource;

                const needsFbEvents = type === FB_APP || type === PIXEL;
                const isDifferentSource = newId !== oldId;

                if (needsFbEvents && isDifferentSource) {
                    this.updateEventDropdownList(newId);
                }
            },
            deep: true,
        },
    },
    computed: {
        ...mapGetters('asset', ['connectionMap']),
        ...mapGetters('proposal', ['baseType']),
        ...mapGetters('campaign', ['intel']),
        fbAssets: R.compose(
            R.flatten,
            R.values,
            R.pick([FB_APP, FB_PAGE, PIXEL]),
            R.prop('connectionMap')
        ),
        defaultMainSource() {
            const assets = R.filter((asset) => {
                const { type, data = {} } = asset;
                const intelData = R.propOr({}, type, this.intel);
                const id =
                    type === FB_PAGE || type === FB_APP
                        ? intelData
                        : intelData.id;

                return R.propEq('id', id, data);
            }, this.fbAssets);

            return makeDefaultOriginData({
                assets,
                baseType: this.baseType,
            });
        },
        mainSource: R.pathOr({}, ['data', 'event_source']),
        mainSourceType: R.pathOr('', ['mainSource', 'type']),
        maxDays() {
            return this.mainSourceType === FB_PAGE ? 365 : 180;
        },
        noEventList() {
            return (
                (this.mainSourceType === FB_APP ||
                    this.mainSourceType === PIXEL) &&
                !isValueExist(this.eventsDropdown)
            );
        },
        inclusionList: R.pathOr([], ['data', 'inclusions']),
        exclusionList: R.pathOr([], ['data', 'exclusions']),
    },
    methods: {
        makeSelectedIndex({ field, id }) {
            return R.findIndex(R.propEq('id', id))(this.data[field]);
        },
        setData(route, value) {
            let data = this.data;
            data = R.assocPath(route, value, data);
            this.$emit('update:data', {
                ...data,
                meta: { dirty: checkRemarketingDirty(data) },
            });
        },
        isFromEvent(type) {
            return R.or(
                R.equals(type, MAIN_TYPES[PIXEL].FROM_EVENT_USERS),
                R.equals(type, MAIN_TYPES[FB_APP].FROM_EVENT_USERS)
            );
        },
        makeDefaultItem(type) {
            let element;
            switch (type) {
                case PIXEL: {
                    const FIRST_ITEM =
                        this.type === 'LOOKALIKE'
                            ? MAIN_TYPES[PIXEL].TOP_TIME_SPENDERS
                            : MAIN_TYPES[PIXEL].ALL_VISITORS;
                    element = SUB_TYPES[PIXEL][FIRST_ITEM];
                    break;
                }
                case FB_APP: {
                    const FIRST_ITEM = MAIN_TYPES[FB_APP].APP_LAUNCHED_USERS;
                    element = SUB_TYPES[FB_APP][FIRST_ITEM];
                    break;
                }
                case FB_PAGE: {
                    const FIRST_ITEM = MAIN_TYPES[FB_PAGE].ENGAGED_USER;
                    element = SUB_TYPES[FB_PAGE][FIRST_ITEM];
                    break;
                }
            }
            element.id = generateId();
            return element;
        },
        renderInputItem(item, field) {
            switch (item.template) {
                case MAIN_TYPES[PIXEL].ALL_VISITORS:
                case MAIN_TYPES[FB_APP].APP_LAUNCHED_USERS:
                case MAIN_TYPES[FB_PAGE].ENGAGED_USER:
                case MAIN_TYPES[FB_PAGE].VISITED_USER:
                case MAIN_TYPES[FB_PAGE].CTA_USER:
                case MAIN_TYPES[FB_PAGE].MESSAGE_USER:
                case MAIN_TYPES[FB_PAGE].SAVED_USER:
                    return this.renderDefaultItem(item, field);
                case MAIN_TYPES[PIXEL].TOP_TIME_SPENDERS:
                case MAIN_TYPES[FB_APP].MOST_ACTIVE_USERS:
                case MAIN_TYPES[FB_APP].TOP_PURCHASE_USERS:
                    return this.renderDoubleDropdownItem(item, field);
                case MAIN_TYPES[FB_APP].FROM_EVENT_USERS:
                case MAIN_TYPES[PIXEL].FROM_EVENT_USERS:
                    return this.renderEventItem(item, field);
                case MAIN_TYPES[PIXEL].VISITORS_BY_URL:
                    return this.renderThirdInputItem(item, field);
            }
        },
        remarketingItemIcon(field) {
            const props = {
                attrs: {
                    viewBox: DEFAULT_SVG_VIEWBOX,
                    width: '15px',
                    height: '15px',
                },
                style: {
                    marginRight: '8px',
                },
            };
            return (
                <SVGWrapper>
                    {field === INCLUSIONS ? (
                        <RoundIcludeIcon {...props} fill={COLORS['blue-60']} />
                    ) : (
                        <RoundExcludeIcon {...props} fill={COLORS['red-60']} />
                    )}
                </SVGWrapper>
            );
        },
        renderMainSouceSelector() {
            const formatter = ({ data = {}, type = '' }) => {
                const textByType = R.propOr('', type, TEXT_BY_TYPES);

                const name = R.propOr('', 'name', data || {});
                const typeLocale = textByType
                    ? this.$t(`CONNECTION.${textByType}`)
                    : '';

                return name && typeLocale
                    ? `${name} | ${typeLocale}`
                    : this.$t('REMARKETING.no_data');
            };

            const onInput = ({ value = {} }) => {
                const { type, id } = value;
                if (!type && !id) {
                    this.setData([], {
                        event_source: {},
                        [INCLUSIONS]: [],
                        [EXCLUSIONS]: [],
                    });
                } else {
                    this.setData([], {
                        event_source: value,
                        [INCLUSIONS]: [this.makeDefaultItem(type)],
                        [EXCLUSIONS]: [],
                    });
                }
            };
            return (
                <MainSourceWrapper>
                    <MainSourceTitle width="20%" style={{ minWidth: '115px' }}>
                        {this.$t('REMARKETING.use_data')}{' '}
                    </MainSourceTitle>
                    <MainSourceDropdown style={{ width: '100%' }}>
                        <Dropdown
                            placeholder={this.$t('REMARKETING.select_data')}
                            formatter={formatter}
                            options={this.fbAssets}
                            value={formatter(this.mainSource)}
                            isLoading={
                                this.mountLoading || this.dropdownLoading
                            }
                            onInput={onInput}
                        />
                    </MainSourceDropdown>
                </MainSourceWrapper>
            );
        },
        renderFirstDropdown({ field, index }) {
            const selectedData = R.pathOr({}, ['data', field, index], this);
            const { id } = selectedData;
            const options = DROPDOWN_ITEM[this.mainSourceType];
            const formatter = ({ type, template }) => {
                const refinedType = type || template;
                return this.$t(
                    R.compose(
                        R.prop('text'),
                        R.find(R.propEq('type', refinedType)),
                        R.propOr([], this.mainSourceType)
                    )(DROPDOWN_ITEM)
                );
            };
            return (
                <Dropdown
                    options={options}
                    value={formatter(selectedData)}
                    formatter={formatter}
                    onInput={({ value = {} }) => {
                        this.setData([field, index], {
                            id,
                            ...SUB_TYPES[this.mainSourceType][value.type],
                        });
                    }}
                />
            );
        },
        renderSecondDropdown({ field, index }) {
            const selectedData = R.pathOr({}, ['data', field, index], this);
            const options = SUB_DROPDOWN_ITEM.percentile;
            const value = R.compose(
                R.find(
                    R.propEq(
                        'value',
                        R.path(['aggregation', 'value', 'from'], selectedData)
                    )
                ),
                R.prop('percentile')
            )(SUB_DROPDOWN_ITEM);
            const formatter = (value) => this.$t(value.text);
            return (
                <Dropdown
                    options={options}
                    value={formatter(value)}
                    formatter={formatter}
                    onInput={({ value = {} }) => {
                        this.setData(
                            [field, index, 'aggregation', 'value', 'from'],
                            value.value
                        );
                    }}
                />
            );
        },
        renderEventDropdown({ field, index }) {
            const selectedData = R.pathOr({}, ['data', field, index], this);
            const options = this.eventsDropdown;
            const value = R.path(['filter', 'value'], selectedData);
            const noEventList = !isValueExist(this.eventsDropdown);
            const placeholder = noEventList
                ? this.$t('REMARKETING.no_event_list')
                : this.$t('REMARKETING.select_data');
            const disabled = noEventList;
            const formatter = (value) => {
                let refiendValue;
                switch (typeof value) {
                    case 'string':
                        refiendValue = value;
                        break;
                    case 'object':
                        refiendValue = value.display_name;
                        break;
                }
                return refiendValue || placeholder;
            };
            return (
                <Dropdown
                    formatter={formatter}
                    placeholder={placeholder}
                    options={options}
                    value={formatter(value)}
                    disabled={disabled}
                    isLoading={this.dropdownLoading}
                    onInput={({ value = {} }) =>
                        this.setData([field, index, 'filter', 'value'], value)
                    }
                />
            );
        },
        renderKeywordsDropdown({ field, index }) {
            const selectedData = R.pathOr({}, ['data', field, index], this);
            const value = R.compose(
                R.find(
                    R.propEq(
                        'value',
                        R.path(['filter', 'operator'], selectedData)
                    )
                ),
                R.prop('keywords')
            )(SUB_DROPDOWN_ITEM);
            const formatter = ({ text = '' }) => this.$t(text);
            return (
                <Dropdown
                    options={SUB_DROPDOWN_ITEM.keywords}
                    formatter={formatter}
                    value={formatter(value)}
                    onInput={({ value = {} }) => {
                        this.setData(
                            [field, index, 'filter', 'operator'],
                            value.value
                        );
                    }}
                />
            );
        },
        makeErrorMessage(item) {
            const { days, template, filter } = item;
            const overMaxdays = days > this.maxDays;
            const isDaysZero = days === '0';
            const noValueAtDays = !days;
            const noValueAtContains =
                template === MAIN_TYPES[PIXEL].VISITORS_BY_URL && !filter.value;

            if (overMaxdays) {
                return this.$t('REMARKETING.over_days', {
                    days: this.maxDays,
                });
            } else if (isDaysZero) {
                return this.$t('REMARKETING.zero_days');
            } else if (noValueAtDays) {
                return this.$t('REMARKETING.no_blank_days');
            } else if (noValueAtContains) {
                return this.$t('REMARKETING.no_field');
            } else {
                return '';
            }
        },
        renderDaysInput(field, index) {
            const selectedData = R.pathOr({}, ['data', field, index], this);
            const { days } = selectedData;
            return (
                <StatusInput
                    counterVisible={false}
                    css={DayInput}
                    mask={'###'}
                    value={days}
                    onInput={(days) => {
                        this.setData([field, index], {
                            ...selectedData,
                            days,
                        });
                    }}
                    placeholder={String(this.maxDays)}
                />
            );
        },
        renderDefaultItem(selectedData, field) {
            const { id } = selectedData;
            const index = this.makeSelectedIndex({ field, id });
            return (
                <RemarketingItemContent>
                    {this.remarketingItemIcon(field)}
                    <ColumnItemWrapper>
                        <MainSourceDropdown margin="0 16px 0 0" width="50%">
                            {this.renderFirstDropdown({
                                field,
                                index,
                            })}
                        </MainSourceDropdown>
                        <DaySettingWrapper>
                            <MainSourceTitle
                                width="13%"
                                style={{ minWidth: '71px', textAlign: 'right' }}
                            >
                                {this.$t('REMARKETING.in_the_past')}
                            </MainSourceTitle>
                            {this.renderDaysInput(field, index)}
                            <MainSourceTitle width="10%">
                                {this.$t('REMARKETING.days')}
                            </MainSourceTitle>
                        </DaySettingWrapper>
                    </ColumnItemWrapper>
                </RemarketingItemContent>
            );
        },
        renderDoubleDropdownItem(selectedData, field) {
            const { id } = selectedData;
            const index = this.makeSelectedIndex({ field, id });
            return (
                <RemarketingItemContent>
                    {this.remarketingItemIcon(field)}
                    <ColumnItemWrapper>
                        <DoubleDropdownWrapper width="50%">
                            <MainSourceDropdown margin="0 2% 0 0" width="65%">
                                {this.renderFirstDropdown({
                                    field,
                                    index,
                                })}
                            </MainSourceDropdown>
                            <MainSourceDropdown width="33%">
                                {this.renderSecondDropdown({ field, index })}
                            </MainSourceDropdown>
                        </DoubleDropdownWrapper>
                        <DaySettingWrapper>
                            <MainSourceTitle
                                width="13%"
                                style={{ minWidth: '71px', textAlign: 'right' }}
                            >
                                {this.$t('REMARKETING.in_the_past')}
                            </MainSourceTitle>
                            {this.renderDaysInput(field, index)}
                            <MainSourceTitle width="10%">
                                {this.$t('REMARKETING.days')}
                            </MainSourceTitle>
                        </DaySettingWrapper>
                    </ColumnItemWrapper>
                </RemarketingItemContent>
            );
        },
        renderEventItem(selectedData, field) {
            const { id } = selectedData;
            const index = this.makeSelectedIndex({ field, id });
            return (
                <RemarketingItemContent>
                    {this.remarketingItemIcon(field)}
                    <ColumnItemWrapper direction="column">
                        <DoubleDropdownWrapper width="100%">
                            <MainSourceDropdown margin="0 2% 0 0" width="65%">
                                {this.renderFirstDropdown({
                                    field,
                                    index,
                                })}
                            </MainSourceDropdown>
                            <MainSourceDropdown width="33%">
                                {this.renderEventDropdown({ field, index })}
                            </MainSourceDropdown>
                        </DoubleDropdownWrapper>
                        <DaySettingWrapper style={{ marginTop: '8px' }}>
                            <MainSourceTitle
                                width={
                                    this.globalUser.language == 'en'
                                        ? '70px'
                                        : '27px'
                                }
                                style={{
                                    textAlign: 'right',
                                }}
                            >
                                {this.$t('REMARKETING.in_the_past')}
                            </MainSourceTitle>
                            {this.renderDaysInput(field, index)}
                            <MainSourceTitle width="10%">
                                {this.$t('REMARKETING.days')}
                            </MainSourceTitle>
                        </DaySettingWrapper>
                    </ColumnItemWrapper>
                </RemarketingItemContent>
            );
        },
        renderThirdInputItem(selectedData, field) {
            const { id } = selectedData;
            const index = this.makeSelectedIndex({ field, id });
            return (
                <RemarketingItemContent>
                    {this.remarketingItemIcon(field)}
                    <ColumnItemWrapper direction="column">
                        <ColumnItemWrapper>
                            <MainSourceDropdown margin="0 16px 0 0" width="50%">
                                {this.renderFirstDropdown({
                                    field,
                                    index,
                                })}
                            </MainSourceDropdown>
                            <DaySettingWrapper>
                                <MainSourceTitle
                                    width="13%"
                                    style={{
                                        minWidth: '71px',
                                        textAlign: 'right',
                                    }}
                                >
                                    {this.$t('REMARKETING.in_the_past')}
                                </MainSourceTitle>
                                {this.renderDaysInput(field, index)}
                                <MainSourceTitle width="10%">
                                    {this.$t('REMARKETING.days')}
                                </MainSourceTitle>
                            </DaySettingWrapper>
                        </ColumnItemWrapper>
                        <div style={{ marginTop: '8px' }}>
                            <DoubleDropdownWrapper>
                                <MainSourceDropdown
                                    margin="0 2% 0 0"
                                    width="28%"
                                >
                                    {this.renderKeywordsDropdown({
                                        field,
                                        index,
                                    })}
                                </MainSourceDropdown>
                                <MainSourceDropdown width="70%">
                                    <StatusInput
                                        css={urlInput}
                                        counterVisible={false}
                                        value={selectedData.filter.value}
                                        onInput={(value) => {
                                            this.setData(
                                                [
                                                    field,
                                                    index,
                                                    'filter',
                                                    'value',
                                                ],
                                                value
                                            );
                                        }}
                                    />
                                </MainSourceDropdown>
                            </DoubleDropdownWrapper>
                        </div>
                    </ColumnItemWrapper>
                </RemarketingItemContent>
            );
        },
        renderReamarketingItem() {
            const remarketingItems = R.pick(
                [INCLUSIONS, EXCLUSIONS],
                this.data
            );
            return (
                <RemarketingItemWrapper>
                    {Object.keys(remarketingItems).map((key) => {
                        const items = remarketingItems[key];
                        return (
                            isValueExist(items) &&
                            items.map((item) => {
                                const errorMessage =
                                    this.makeErrorMessage(item);
                                return [
                                    <RemaketingItem>
                                        {this.renderInputItem(item, key)}
                                        <SVGWrapper
                                            onClick={() =>
                                                this.onClickDeleteItem(
                                                    key,
                                                    item.id
                                                )
                                            }
                                        >
                                            <CloseIcon
                                                v-view-box
                                                fill={COLORS['gray-40']}
                                                width="12px"
                                                height="12px"
                                            />
                                        </SVGWrapper>
                                    </RemaketingItem>,
                                    errorMessage && (
                                        <ErrorMessage>
                                            {errorMessage}
                                        </ErrorMessage>
                                    ),
                                ];
                            })
                        );
                    })}
                    {isValueExist(this.mainSource) && this.renderAddBtns()}
                </RemarketingItemWrapper>
            );
        },
        renderAddBtns() {
            const noInclusion = this.inclusionList.length < 1;
            return (
                <div style={{ display: 'flex' }}>
                    {isValueExist(this.inclusionList) < MAX_NUM_OF_INCLUDED && (
                        <AddItemBtnWrapper
                            onClick={() => this.onClickAdjustPeople(INCLUSIONS)}
                        >
                            <RoundIcludeIcon
                                v-view-box
                                fill={COLORS['blue-60']}
                                width="15px"
                                height="15px"
                            />
                            <AddItemBtnText color={COLORS['blue-60']}>
                                {this.$t('REMARKETING.include_more_people')}
                            </AddItemBtnText>
                        </AddItemBtnWrapper>
                    )}
                    {isValueExist(this.exclusionList) < MAX_NUM_OF_EXCLUDED &&
                        this.wrapTooltip({
                            visible: noInclusion,
                            title: this.$t(
                                'REMARKETING.block_exclusion_no_inclusion'
                            ),
                            content: (
                                <AddItemBtnWrapper
                                    onClick={() =>
                                        noInclusion
                                            ? R.identity
                                            : this.onClickAdjustPeople(
                                                  EXCLUSIONS
                                              )
                                    }
                                    noInclusion={noInclusion}
                                >
                                    <RoundExcludeIcon
                                        v-view-box
                                        fill={COLORS['blue-60']}
                                        width="15px"
                                        height="15px"
                                    />
                                    <AddItemBtnText color={COLORS['blue-60']}>
                                        {this.$t(
                                            'REMARKETING.exclude_more_people'
                                        )}
                                    </AddItemBtnText>
                                </AddItemBtnWrapper>
                            ),
                        })}
                </div>
            );
        },
        onClickAdjustPeople(field) {
            const fieldLength = R.pathOr(0, ['data', field, 'length'], this);
            const maxLimit =
                field === INCLUSIONS
                    ? MAX_NUM_OF_INCLUDED
                    : MAX_NUM_OF_EXCLUDED;

            if (fieldLength >= maxLimit) return;

            this.setData(
                [field],
                R.append(
                    this.makeDefaultItem(this.mainSourceType),
                    this.data[field]
                )
            );
        },
        onClickDeleteItem(field, id) {
            this.setData(
                [field],
                R.reject(R.propEq('id', id), this.data[field])
            );
        },
        updateEventDropdownList(id) {
            this.dropdownLoading = true;
            AssetService.facebookEvents(id)
                .then((data = []) => {
                    if (isValueExist(data)) {
                        this.eventsDropdown = data;
                    }
                })
                .catch(console.error)
                .finally(() => (this.dropdownLoading = false));
        },
    },
    created() {
        if (!isValueExist(this.data)) {
            const { type } = this.defaultMainSource;
            setTimeout(() => {
                this.setData([], {
                    event_source: this.defaultMainSource,
                    [INCLUSIONS]: [this.makeDefaultItem(type)],
                    [EXCLUSIONS]: [],
                });
            }, 200);
        }

        if (this.mainSourceType === FB_APP || this.mainSourceType === PIXEL) {
            this.updateEventDropdownList(this.mainSource.id);
        }
    },
    render() {
        return (
            <Wrapper>
                {this.type === 'REMARKETING' && (
                    <Title>
                        {this.$t('REMARKETING.header_title_remarketing')}
                    </Title>
                )}
                {this.renderMainSouceSelector()}
                {this.renderReamarketingItem()}
            </Wrapper>
        );
    },
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
`;

const Title = styled.p`
    color: ${COLORS['gray-100']};
    font-size: 14px;
    font-weight: 500;
`;

const MainSourceWrapper = styled.div`
    display: flex;
    width: 100%;
    align-items: center;
    margin-top: 16px;
    @media ${ON_MOBILE} {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const MainSourceTitle = styled('span', { width: String })`
    color: ${COLORS['gray-100']};
    font-size: 14px;
    margin-right: 16px;
    width: ${({ width }) => width || 'inherit'};
    font-weight: 400;
    @media ${ON_MOBILE} {
        margin-bottom: 8px;
    }
`;

const MainSourceDropdown = styled('div', { width: String, margin: String })`
    width: ${({ width }) => width || '100%'};
    margin: ${({ margin }) => margin || 'unset'};
    @media ${ON_MOBILE} {
        width: 90%;
    }
`;

const RemarketingItemWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 16px;
`;

const AddItemBtnWrapper = styled('div', { noInclusion: Boolean })`
    display: flex;
    align-items: center;
    margin-top: 8px;
    font-size: 14px;
    margin-right: 16px;
    color: ${COLORS['blue-60']};
    cursor: ${({ noInclusion }) => (noInclusion ? 'not-allowed' : 'pointer')};
`;

const AddItemBtnText = styled('span', { color: String })`
    margin-left: 4px;
    font-size: 13px;
    font-weight: 500;
    color: ${({ color }) => color || COLORS['gray-100']};
`;

const RemaketingItem = styled.div`
    display: flex;
    align-items: center;
    border-top: 1px solid ${COLORS['gray-15']};
    padding: 16px 0;
    justify-content: space-between;
`;

const RemarketingItemContent = styled('div', { direction: String })`
    display: flex;
    width: 90%;
    align-items: center;
    flex-direction: ${({ direction }) => direction || 'row'};
    @media ${ON_MOBILE} {
        align-items: flex-start;
    }
`;

const ColumnItemWrapper = styled('div', { direction: String })`
    display: flex;
    width: 100%;
    flex-direction: ${({ direction }) =>
        direction === 'column' ? 'column' : 'initial'};
    @media ${ON_MOBILE} {
        flex-direction: column;
    }
`;

const DoubleDropdownWrapper = styled('div', { width: String })`
    width: ${({ width }) => width || '100%'};
    display: flex;
    margin-right: 16px;
    & .ant-dropdown-trigger {
        min-width: unset;
    }
    @media ${ON_MOBILE} {
        flex-direction: column;
        width: 100%;
        max-width: unset;
        & > :first-child {
            margin-bottom: 8px;
            margin-right: unset;
        }
    }
`;

const DaySettingWrapper = styled.div`
    display: flex;
    align-items: center;

    @media ${ON_MOBILE} {
        margin-top: 8px;
    }
`;

const DayInput = css`
    width: 12%;
    margin-right: 8px;
    min-width: 60px;
    input {
        border-bottom: unset;
        background: #fff;
        border: 1px solid ${COLORS['gray-30']};
        height: 36px;
    }
`;

const urlInput = css`
    width: 100%;
    margin-right: 8px;
    min-width: 60px;
    input {
        border-bottom: unset;
        background: #fff;
        border: 1px solid ${COLORS['gray-30']};
        height: 36px;
    }
`;

const SVGWrapper = styled.div`
    @media ${ON_MOBILE} {
        align-self: flex-start;
        margin-top: 8px;
        margin-left: 8px;
    }
`;

const ErrorMessage = styled.div`
    margin-bottom: 8px;
    color: ${COLORS['red-60']};
    font-size: 13px;
`;
